import React, { useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body3Light, Body3Regular } from '../../lib/font';
import ProfileImage from '../ProfileImage';
import { I_CIRCLE_EXIT, I_EMPTY_V2, I_GREY_CIRCLE, I_WHITE_CHECK } from '../../types/images';
import styled from 'styled-components';
import CoachReportSheet from '../../scenes/OpsBriefing/CoachReportSheet';
import { useCoachReportAccess } from '../../scenes/OpsBriefing/useCoachReportAccess';

/**
 * 월간 리포트 칸 폭.
 *
 * 이 표는 1192px 고정입니다. 칸을 하나 더 놓을 자리를 만들려고
 * 구분선 좌우 여백을 24 → 14 로 줄였습니다 (6칸 × 20px = 120px 확보).
 * 데이터 칸은 하나도 안 줄였습니다 — 이메일 칸을 줄이면 말줄임 처리가
 * 없어서 긴 주소가 옆 칸을 밀고 들어갑니다.
 */
const REPORT_COL_WIDTH = 104;

/**
 * 버튼을 칸 안쪽으로 들여놓는 폭 (2026-09-01 QA35).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 표가 아니라 버튼을 줄이나
 * ─────────────────────────────────────────────────────────────
 * 이 표는 왼쪽에만 여백이 있었다 — `padding-left: 24px` 에 오른쪽은 0 이라,
 * 마지막 칸의 버튼이 표 끝선에 닿아 보였다.
 *
 * 그런데 **오른쪽에 넣을 자리가 없다.** 칸 폭을 다 더하면 1165px 이고
 * 내용 영역은 1168px(1192 - 왼쪽 패딩 24)라 여유가 **3px** 뿐이다.
 * `box-sizing: border-box` 라 여기에 `padding-right` 를 주면 그만큼 내용
 * 영역이 줄고, 칸들이 flex-shrink 로 조금씩 눌려 **여섯 칸의 세로줄이
 * 전부 어긋난다**(이 파일 위 주석대로 구분선을 24 → 14 까지 깎아서 겨우
 * 맞춰 놓은 배치다).
 *
 * 그래서 칸(104px)은 그대로 두고 **버튼만** 안으로 들인다. 칸이 안 변하니
 * 머리말 「월간 리포트」와 세로 중심도 그대로다. 남은 3px 과 합쳐 표
 * 끝선에서 약 11px 떨어진다.
 *
 * ⚠️ 표를 넓혀서 해결하지 마라. 바깥 상자도 1192 고정이고 `overflow: hidden`
 *    이라, 행만 넓히면 오른쪽이 잘린다.
 */
const REPORT_BUTTON_INSET = 8;
const REPORT_BUTTON_WIDTH = REPORT_COL_WIDTH - REPORT_BUTTON_INSET * 2;

const StyledRowContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 1192px;
    height: 56px;
    align-items: center;
    padding-left: 24px;
    cursor: pointer;
    background-color: #fdfeff;
    &:hover {
        background-color: #ecf1fc;
    }
    &:active {
        background-color: #f0f6ff;
    }
`;

const TrainerList = ({ filterCoachList, onClickDetailPage }: any) => {
    /*
     * 월간 리포트 — 운영 브리핑의 완성본 A4 3장을 그대로 연다.
     *
     * ⚠️ '성과 리포트'라고 부르지 않는다.
     * 강사 **상세** 화면(TrainerProfileComponent)에 이미 '성과 리포트' 버튼이 있고,
     * 그건 전혀 다른 곳(/centerReport/:coachId 웹 화면)으로 간다. 같은 기능 안에서
     * 같은 이름이 다른 화면을 열면 원장님은 둘 중 하나를 고장으로 읽는다.
     * 이 리포트의 표지가 실제로 MONTHLY COACH PERFORMANCE REPORT 라 이름도 정확하다.
     *
     * 이 목록 API(/v1/centers/coach-list)는 리포트가 필요로 하는 집계를 주지 않는다.
     * 그렇다고 여기서 집계를 따로 불러 조립하면 리포트에 먹일 데이터를 만드는
     * 규칙이 화면마다 생겨, 결국 또 다른 숫자가 인쇄된다.
     * **coachId 만 넘기고 찾는 일까지 시트가 한다** (CoachReportSheet 주석 참고).
     */
    const canOpenReport = useCoachReportAccess();
    const [reportCoachId, setReportCoachId] = useState<string | null>(null);

    return (
        <Col
            style={{
                width: 1192,
                minHeight: 240,
                borderRadius: 16,
                border: '1px solid #c3d4f4',
                marginTop: 24,
                backgroundColor: colors.WHITE_50,
                overflow: 'hidden',
            }}
        >
            <Row
                style={{
                    width: 1192,
                    height: 56,
                    borderBottom: '1px solid #c3d4f4',
                    alignItems: 'center',
                    paddingLeft: 24,
                }}
            >
                <Body3Regular style={{ width: 108, textAlign: 'center' }}>{'강사 이름'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />

                <Body3Regular style={{ width: 80, textAlign: 'center' }}>{'강사 코드'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />

                <Body3Regular style={{ width: 88, textAlign: 'center' }}>{'총 회원 수'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />

                <Body3Regular style={{ width: 88, textAlign: 'center' }}>{'활성 회원 수'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />

                <Body3Regular style={{ width: 162, textAlign: 'center' }}>{'연락처'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />

                <Body3Regular style={{ width: 260, textAlign: 'center' }}>{'이메일'}</Body3Regular>
                <div
                    style={{
                        width: 1,
                        height: 24,
                        backgroundColor: colors.BLUE_100,
                        marginLeft: 14,
                        marginRight: 14,
                    }}
                />
                <Body3Regular style={{ width: 72, textAlign: 'center' }}>{'센터 연결'}</Body3Regular>
                {/*
                  월간 리포트 칸.
                  노출 게이트가 닫힌 센터에서는 머리글까지 통째로 빠진다 —
                  버튼만 숨기고 빈 칸을 남기면 "여기 뭐가 있어야 하는데" 로 읽힌다.
                */}
                {canOpenReport && (
                    <>
                        <div
                            style={{
                                width: 1,
                                height: 24,
                                backgroundColor: colors.BLUE_100,
                                marginLeft: 14,
                                marginRight: 14,
                            }}
                        />
                        <Body3Regular style={{ width: REPORT_COL_WIDTH, textAlign: 'center' }}>
                            {'월간 리포트'}
                        </Body3Regular>
                    </>
                )}
            </Row>

            <Col>
                {/*
                 * isConnected : false , isDeleted :false -> 수락 대기중 상태
                 * isConnected : true , isDeleted :false -> 정상 활성화 상태
                 * isConnected : true , isDeleted :true -> 비활성화 상태 (센터에서 삭제된 트레이너)
                 * isConnected : false , isDeleted :true -> 강사가 탈퇴한 상태 (센터에서 삭제 + 트레이너라포 탈퇴)
                 **/}
                {filterCoachList.length > 0 ? (
                    filterCoachList.map((item: any, index: number) => {
                        return item?.isDeleted ? (
                            <Row
                                style={{
                                    width: 1192,
                                    height: 56,
                                    alignItems: 'center',
                                    paddingLeft: 24,
                                    backgroundColor: colors.GREY_50,
                                }}
                                key={'coachList' + index}
                            >
                                <Row style={{ width: 108, justifyContent: 'center' }}>
                                    <ProfileImage
                                        imageUrl={item?.profileImageUrl}
                                        type="VerySmall"
                                        profileName={item?.profileName}
                                        profileColorType={item?.profileColorType}
                                    />
                                    <Body3Regular style={{ marginLeft: 12, color: colors.LAVEL_1 }}>
                                        {`${item?.coachName}`}
                                    </Body3Regular>
                                </Row>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{
                                        width: 80,
                                        textAlign: 'center',
                                        color: colors.ERROR_200,
                                    }}
                                >{`삭제`}</Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{
                                        width: 88,
                                        textAlign: 'center',
                                        color: colors.LAVEL_1,
                                    }}
                                >
                                    {item?.isDeleted ? `-` : `${item?.coachTotalMemberCount}명`}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{
                                        width: 88,
                                        textAlign: 'center',
                                        color: colors.LAVEL_1,
                                    }}
                                >
                                    {item?.isDeleted ? `-` : `${item?.coachActiveMemberCount}명`}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{
                                        width: 160,
                                        textAlign: 'center',
                                        color: colors.LAVEL_1,
                                    }}
                                >
                                    {item?.coachPhoneNumber ? item?.coachPhoneNumber : '-'}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{
                                        width: 260,
                                        textAlign: 'center',
                                        color: colors.LAVEL_1,
                                    }}
                                >
                                    {item?.coachEmail ? item?.coachEmail : '-'}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.GREY_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />
                                <Row style={{ width: 72, justifyContent: 'center' }}>
                                    <img src={I_CIRCLE_EXIT} style={{ width: 24, height: 24 }} />
                                </Row>
                                {/*
                                  삭제된 강사에게는 리포트를 열지 않는다.
                                  칸은 비워 두되 폭은 그대로 둬야 위 줄들과 세로가 맞는다.
                                */}
                                {canOpenReport && (
                                    <div style={{ width: REPORT_COL_WIDTH + 29, flex: 'none' }} />
                                )}
                            </Row>
                        ) : (
                            <StyledRowContainer onClick={() => onClickDetailPage(item)} key={'coachList' + index}>
                                <Row style={{ width: 108, justifyContent: 'center' }}>
                                    <ProfileImage
                                        imageUrl={item?.profileImageUrl}
                                        type="VerySmall"
                                        profileName={item?.profileName}
                                        profileColorType={item?.profileColorType}
                                    />
                                    <Body3Regular style={{ marginLeft: 12, color: colors.LAVEL_3 }}>
                                        {`${item?.coachName}`}
                                    </Body3Regular>
                                </Row>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{ width: 80, textAlign: 'center' }}
                                >{`${item?.coachInviteCode}`}</Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{ width: 88, textAlign: 'center' }}
                                >{`${item?.coachTotalMemberCount}명`}</Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular
                                    style={{ width: 88, textAlign: 'center' }}
                                >{`${item?.coachActiveMemberCount}명`}</Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular style={{ width: 160, textAlign: 'center' }}>
                                    {item?.coachPhoneNumber ? item?.coachPhoneNumber : '-'}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Body3Regular style={{ width: 260, textAlign: 'center' }}>
                                    {item?.coachEmail ? item?.coachEmail : '-'}
                                </Body3Regular>
                                <div
                                    style={{
                                        width: 1,
                                        height: 24,
                                        backgroundColor: colors.WHITE_50,
                                        marginLeft: 14,
                                        marginRight: 14,
                                    }}
                                />

                                <Row style={{ width: 72, justifyContent: 'center' }}>
                                    {item?.isConnected === true && item?.isDeleted === false ? (
                                        <Row
                                            style={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: 9,
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: colors.BLUE_500,
                                            }}
                                        >
                                            <img src={I_WHITE_CHECK} style={{ width: 16, height: 16 }} />
                                        </Row>
                                    ) : (
                                        <img src={I_CIRCLE_EXIT} style={{ width: 24, height: 24 }} />
                                    )}
                                </Row>
                                {canOpenReport && (
                                    <>
                                        <div
                                            style={{
                                                width: 1,
                                                height: 24,
                                                backgroundColor: colors.WHITE_50,
                                                marginLeft: 14,
                                                marginRight: 14,
                                            }}
                                        />
                                        <Row
                                            style={{ width: REPORT_COL_WIDTH, justifyContent: 'center' }}
                                        >
                                            <button
                                                type="button"
                                                aria-label={`${item?.coachName} 강사 월간 리포트 열기`}
                                                /*
                                                  ⚠️ 행 전체에 onClick(강사 상세로 이동)이 걸려 있다.
                                                     막지 않으면 리포트를 누른 순간 상세 화면으로도 같이 넘어간다.
                                                */
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setReportCoachId(item?.coachId ?? null);
                                                }}
                                                disabled={!item?.coachId}
                                                style={{
                                                    height: 30,
                                                    /* 칸(104)은 그대로, 버튼만 안으로 — 위 상수 주석 참고 */
                                                    width: REPORT_BUTTON_WIDTH,
                                                    borderRadius: 8,
                                                    border: `1px solid ${item?.coachId ? '#C3D4F4' : '#E2E8F0'}`,
                                                    background: item?.coachId ? '#FFFFFF' : '#F2F4F6',
                                                    color: item?.coachId ? '#3D73DD' : '#A5ABB5',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    cursor: item?.coachId ? 'pointer' : 'not-allowed',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                월간 리포트
                                            </button>
                                        </Row>
                                    </>
                                )}
                            </StyledRowContainer>
                        );
                    })
                ) : (
                    <Col
                        style={{
                            width: 192,
                            height: 56,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            alignSelf: 'center',
                            marginTop: 56,
                        }}
                    >
                        <img style={{ width: 24, height: 24 }} src={I_EMPTY_V2} />
                        <Body3Light style={{ color: colors.LAVEL_2 }}>{'아직 등록된 강사님이 없어요!'}</Body3Light>
                    </Col>
                )}
            </Col>

            {/*
              리포트는 문서 최상단에 포털로 뜬다 (CoachPdfReport).
              이 표가 1192px 안에 갇혀 있어도 A4 지면은 온전히 펼쳐진다.
            */}
            {reportCoachId && (
                <CoachReportSheet coachId={reportCoachId} onClose={() => setReportCoachId(null)} />
            )}
        </Col>
    );
};

export default TrainerList;
